import { useState } from "react";
import { Search } from "lucide-react";

export const fmt = (n) =>
  `RD$ ${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const waPhone = (p) => {
  const d = String(p || "").replace(/\D/g, "");
  return d.length === 10 ? `1${d}` : d;
};

export const waLink = (phone, msg) => `https://wa.me/${waPhone(phone)}?text=${encodeURIComponent(msg)}`;

const STATUS_STYLES = {
  pagado: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pagada: "bg-emerald-50 text-emerald-700 border-emerald-200",
  activo: "bg-emerald-50 text-emerald-700 border-emerald-200",
  en_revision: "bg-amber-50 text-amber-700 border-amber-200",
  pendiente: "bg-rose-50 text-rose-700 border-rose-200",
  rechazado: "bg-slate-100 text-slate-600 border-slate-300",
  anulada: "bg-slate-100 text-slate-600 border-slate-300",
  retirado: "bg-slate-100 text-slate-600 border-slate-300",
  programada: "bg-sky-50 text-sky-700 border-sky-200",
};

const STATUS_LABELS = {
  pagado: "PAGADO",
  pagada: "PAGADA",
  activo: "ACTIVO",
  en_revision: "EN REVISIÓN",
  pendiente: "PENDIENTE",
  rechazado: "RECHAZADO",
  anulada: "ANULADA",
  retirado: "RETIRADO",
  programada: "PROGRAMADA",
};

export function StatusBadge({ value }) {
  const key = String(value || "").toLowerCase();
  const style = STATUS_STYLES[key] || "bg-slate-100 text-slate-600 border-slate-300";
  const label = STATUS_LABELS[key] || String(value || "—");
  return (
    <span
      data-testid={`status-badge-${key}`}
      className={`inline-flex items-center text-xs font-medium px-2.5 py-0.5 rounded-full border ${style}`}
    >
      {label}
    </span>
  );
}

export function PageHeader({ title, subtitle, children }) {  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="page-title">
          {title}
        </h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>
      {children && <div className="flex gap-2">{children}</div>}
    </div>
  );
}

export function DataTable({ columns, rows, testid, onRowClick, searchable = false, searchPlaceholder = "Buscar…" }) {
  const [q, setQ] = useState("");
  const needle = q.trim().toLowerCase();
  const filtered = !needle ? rows : rows.filter((row) =>
    Object.values(row).some((v) => (typeof v === "string" || typeof v === "number") && String(v).toLowerCase().includes(needle))
  );
  return (
    <div>
      {searchable && (
        <div className="relative mb-3 max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid={`${testid}-search`}
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30"
            placeholder={searchPlaceholder}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid={testid}>
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              {columns.map((c) => (
                <th key={c.key} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-slate-400">
                  {needle ? "Sin resultados para la búsqueda" : "Sin registros"}
                </td>
              </tr>
            )}
            {filtered.map((row) => (
              <tr
                key={row.id}
                className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${onRowClick ? "cursor-pointer" : ""}`}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((c) => (
                  <td key={c.key} className="px-4 py-3 text-slate-700 whitespace-nowrap">
                    {c.render ? c.render(row) : row[c.key] ?? "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
